import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { densityPad, resolveColumns } from "@/lib/pageViews";
import { cn } from "@/lib/utils";
import { ArrowUp, ArrowDown } from "lucide-react";

// value -> comparable
function sortRows(rows, columns, sort) {
  if (!sort?.key) return rows;
  const col = columns.find((c) => c.key === sort.key);
  const acc = col?.accessor || ((r) => r[sort.key]);
  const dir = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = acc(a), vb = acc(b);
    if (va == null) return 1; if (vb == null) return -1;
    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
  });
}

function Cell({ col, row }) {
  return col.cell ? col.cell(row) : <span className="text-foreground">{col.accessor ? col.accessor(row) : row[col.key]}</span>;
}

export function DataView({ pageDef, state, update, rows, onRowClick, emptyText }) {
  const columns = useMemo(() => resolveColumns(pageDef, state), [pageDef, state]);
  const sorted = useMemo(() => sortRows(rows, columns, state.sort), [rows, columns, state.sort]);
  const pad = densityPad(state.density);
  const primary = columns[0];
  const [drag, setDrag] = useState(null);

  const widthOf = (key) => (drag?.key === key ? drag.w : state.columns?.widths?.[key]);
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.closest("th");
    const startW = state.columns?.widths?.[key] || (th ? th.getBoundingClientRect().width : 160);
    const startX = e.clientX;
    const compute = (ev) => Math.max(80, Math.round(startW + (ev.clientX - startX)));
    const onMove = (ev) => setDrag({ key, w: compute(ev) });
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      const w = compute(ev);
      update((s) => ({ ...s, columns: { ...s.columns, widths: { ...(s.columns?.widths || {}), [key]: w } } }));
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
  };
  const resetWidth = (key) => update((s) => {
    const widths = { ...(s.columns?.widths || {}) };
    delete widths[key];
    return { ...s, columns: { ...s.columns, widths } };
  });

  if (!rows.length) {
    return <Card className="px-6 py-16 text-center text-sm text-muted-foreground shadow-soft" data-testid="dataview-empty">{emptyText}</Card>;
  }

  // ---- TABLE ----
  if (state.view === "table") {
    const toggleSort = (key) => update((s) => ({ ...s, sort: { key, dir: s.sort?.key === key && s.sort.dir === "asc" ? "desc" : "asc" } }));
    const hasWidths = columns.some((c) => widthOf(c.key));
    return (
      <Card className="overflow-hidden shadow-soft" data-testid="dataview-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: hasWidths ? "fixed" : "auto", minWidth: "640px" }}>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                {columns.map((c) => {
                  const w = widthOf(c.key);
                  return (
                    <th key={c.key} className="relative select-none px-5 py-3 font-medium" style={w ? { width: `${w}px` } : undefined}>
                      <button className="flex items-center gap-1 hover:text-foreground" onClick={() => c.sortable !== false && toggleSort(c.key)} data-testid={`sort-${c.key}`}>
                        <span className="truncate">{c.label}</span>
                        {state.sort?.key === c.key && (state.sort.dir === "asc" ? <ArrowUp className="h-3 w-3 shrink-0" /> : <ArrowDown className="h-3 w-3 shrink-0" />)}
                      </button>
                      <span onMouseDown={(e) => startResize(e, c.key)} onDoubleClick={() => resetWidth(c.key)}
                        title="Drag to resize · double-click to reset" data-testid={`resize-${c.key}`}
                        className={cn(
                          "absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none",
                          "after:absolute after:right-0 after:top-1/2 after:h-1/2 after:w-px after:-translate-y-1/2 after:bg-border after:transition-all",
                          "hover:after:h-full hover:after:w-[3px] hover:after:bg-accent",
                          drag?.key === c.key && "after:h-full after:w-[3px] after:bg-accent")} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((row) => (
                <tr key={row.id} onClick={() => onRowClick?.(row)}
                  className={cn("transition-colors hover:bg-muted/40", onRowClick && "cursor-pointer")} data-testid={`row-${row.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={cn("overflow-hidden px-5", pad)} style={widthOf(c.key) ? { width: `${widthOf(c.key)}px` } : undefined}>
                      <Cell col={c} row={row} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  // ---- COMPACT LIST ----
  if (state.view === "compact") {
    return (
      <Card className="divide-y divide-border overflow-hidden shadow-soft" data-testid="dataview-compact">
        {sorted.map((row) => (
          <button key={row.id} onClick={() => onRowClick?.(row)} className={cn("flex w-full items-center gap-3 px-4 text-left hover:bg-muted/40", pad)} data-testid={`row-${row.id}`}>
            <div className="min-w-0 flex-1"><Cell col={primary} row={row} /></div>
            <div className="hidden items-center gap-4 sm:flex">
              {columns.slice(1, 4).map((c) => <div key={c.key} className="text-xs text-muted-foreground"><Cell col={c} row={row} /></div>)}
            </div>
          </button>
        ))}
      </Card>
    );
  }

  // ---- CARDS ----
  if (state.view === "cards") {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="dataview-cards">
        {sorted.map((row) => (
          <Card key={row.id} onClick={() => onRowClick?.(row)} className={cn("p-4 shadow-soft transition-colors hover:border-accent/60", onRowClick && "cursor-pointer")} data-testid={`row-${row.id}`}>
            <div className="mb-3"><Cell col={primary} row={row} /></div>
            <div className="space-y-1.5">
              {columns.slice(1).map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">{c.label}</span>
                  <span className="truncate text-right"><Cell col={c} row={row} /></span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    );
  }

  // ---- BOARD (group by a field) ----
  if (state.view === "board") {
    const groupKey = state.groupBy || pageDef.defaultGroupBy || columns.find((c) => c.groupable)?.key || columns[0].key;
    const groupCol = pageDef.columns.find((c) => c.key === groupKey);
    const groupAcc = groupCol?.accessor || ((r) => r[groupKey]);
    const groups = {};
    sorted.forEach((r) => { const g = groupAcc(r) || "—"; (groups[g] ||= []).push(r); });
    return (
      <div className="flex gap-4 overflow-x-auto pb-2" data-testid="dataview-board">
        {Object.entries(groups).map(([g, items]) => (
          <div key={g} className="w-72 shrink-0" data-testid={`board-col-${g}`}>
            <div className="mb-2 flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-sm font-semibold text-foreground">{g}</span>
              <span className="rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((row) => (
                <Card key={row.id} onClick={() => onRowClick?.(row)} className={cn("p-3 shadow-soft", onRowClick && "cursor-pointer")} data-testid={`row-${row.id}`}>
                  <Cell col={primary} row={row} />
                  <div className="mt-2 space-y-1">
                    {columns.slice(1, 3).filter((c) => c.key !== groupKey).map((c) => (
                      <div key={c.key} className="text-xs text-muted-foreground"><Cell col={c} row={row} /></div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- CALENDAR (by date field) ----
  if (state.view === "calendar") {
    const dateKey = pageDef.dateField || columns.find((c) => c.type === "date")?.key;
    const dateAcc = pageDef.columns.find((c) => c.key === dateKey)?.rawDate || ((r) => r[dateKey]);
    const now = new Date();
    const year = now.getFullYear(), month = now.getMonth();
    const first = new Date(year, month, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const byDay = {};
    sorted.forEach((r) => {
      const d = dateAcc(r); if (!d) return;
      const dt = new Date(d);
      if (dt.getFullYear() === year && dt.getMonth() === month) (byDay[dt.getDate()] ||= []).push(r);
    });
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return (
      <Card className="p-4 shadow-soft" data-testid="dataview-calendar">
        <div className="mb-3 font-heading text-lg font-semibold">{first.toLocaleString(undefined, { month: "long", year: "numeric" })}</div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="pb-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => (
            <div key={i} className={cn("min-h-[76px] rounded-lg border p-1.5", d ? "border-border" : "border-transparent")}>
              {d && <div className="mb-1 text-xs text-muted-foreground">{d}</div>}
              <div className="space-y-1">
                {(byDay[d] || []).slice(0, 3).map((row) => (
                  <button key={row.id} onClick={() => onRowClick?.(row)} className="block w-full truncate rounded bg-accent-subtle px-1.5 py-0.5 text-left text-[11px] text-foreground" data-testid={`row-${row.id}`}>
                    {primary.accessor ? primary.accessor(row) : "Item"}
                  </button>
                ))}
                {(byDay[d]?.length || 0) > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{byDay[d].length - 3} more</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return null;
}
